import { useEffect, useState, useMemo, useCallback } from "react";
import { fetchStages } from "./service";
import { Tooltip } from "@mui/material";

const FtmStages = ({ currentStage, stagesList = null }) => {
    const [stages, setStages] = useState(stagesList);

    // Fetch stages only if no list is provided
    useEffect(() => {
        if (!stagesList) {
            fetchStages()
                .then((res) => setStages(res?.stages || []))
                .catch((err) => console.error(err));
        }
    }, [stagesList]);

    // Derive filtered data and color in one place
    const { ftmStages, otherFtmStages, stageIndex, isOtherStage } = useMemo(() => {
        if (!stages || !Array.isArray(stages)) {
            return {
                ftmStages: [],
                otherFtmStages: [],
                stageIndex: -1,
                isOtherStage: false
            };
        }

        const transformStage = (stage) => ({
            ftm_stage_id: stage.ftm_stage_id,
            ftm_stage_name: stage.ftm_stage_name,
            ftm_stage_icon: stage.ftm_stage_icon,
            ftm_stage_color: stage.ftm_stage_color,
            ftm_stage_is_in_progress_flow: stage.ftm_stage_is_in_progress_flow,
        });

        const ftmStages = [];
        const otherFtmStages = [];

        // Single iteration to separate stages
        stages.forEach(stage => {
            const transformedStage = transformStage(stage);
            if (stage.ftm_stage_is_in_progress_flow === 1) {
                ftmStages.push(transformedStage);
            } else {
                otherFtmStages.push(transformedStage);
            }
        });

        const stageIndex = ftmStages?.findIndex(
            step => step.ftm_stage_id === currentStage
        ) ?? -1;

        const isOtherStage = otherFtmStages?.some(
            step => step.ftm_stage_id === currentStage
        );

        return {
            ftmStages,
            otherFtmStages,
            stageIndex,
            isOtherStage
        };
    }, [stages, currentStage]);

    const getCurrentStageColor = useCallback(() => {
        if (stageIndex >= 0 && ftmStages[stageIndex]) {
            return ftmStages[stageIndex].ftm_stage_color;
        }
        return '#ccc';
    }, [ftmStages, stageIndex]);

    const getStatusConfig = useCallback((stage) => {
        const isCurrent = stage?.ftm_stage_id === currentStage;

        if (isOtherStage && isCurrent) {
            return {
                borderColor: stage.ftm_stage_color,
                iconColor: '#fff',
                bgColor: stage.ftm_stage_color
            };
        }

        if (!isOtherStage) {
            const ftmStageIndex = ftmStages?.findIndex(
                s => s.ftm_stage_id === stage.ftm_stage_id
            );

            const isCompleted = stage.ftm_stage_is_in_progress_flow && ftmStageIndex <= stageIndex;

            if (isCompleted) {
                const currentStageColor = getCurrentStageColor();
                return {
                    borderColor: currentStageColor,
                    iconColor: '#fff',
                    bgColor: currentStageColor
                };
            }
        }

        return {
            borderColor: '#ccc',
            iconColor: '#d0d0d0',
            bgColor: '#fff'
        };
    }, [currentStage, isOtherStage, ftmStages, stageIndex, getCurrentStageColor]);

    const getTooltipTitle = useCallback(() => {
        if (!isOtherStage && stageIndex >= 0) {
            return ftmStages[stageIndex]?.ftm_stage_name || '';
        }
        const currentOtherStage = otherFtmStages?.find(
            stage => stage.ftm_stage_id === currentStage
        );
        return currentOtherStage?.ftm_stage_name || '';
    }, [isOtherStage, stageIndex, ftmStages]);

    const renderStatusIcon = useCallback((stage) => {
        const {
            borderColor,
            iconColor,
            bgColor,
        } = getStatusConfig(stage);
        const tooltipTitle = getTooltipTitle();

        return (
            <Tooltip
                key={stage?.ftm_stage_id}
                title={tooltipTitle}
                arrow
                placement="right"
            >
                <div
                    className="avatar avatar-xs rounded-circle ms-1"
                    style={{
                        backgroundColor: bgColor,
                        border: `2px solid ${borderColor}`,
                    }}
                >
                    <i
                        className="material-icons opacity-10 text-xs"
                        style={{
                            color: iconColor,
                        }}
                    >
                        {stage.ftm_stage_icon}
                    </i>
                </div>
            </Tooltip>
        );
    }, [getStatusConfig, getTooltipTitle]);

    return (
        <div className="d-flex align-items-center justify-content-sm-end">
            <div className="d-flex align-items-center">
                {ftmStages.map(renderStatusIcon)}
            </div>
            {otherFtmStages.length > 0 && (
                <>
                    {ftmStages.length > 0 && (
                        <div className="vr ms-2 me-2" />
                    )}
                    <div className="d-flex align-items-center">
                        {otherFtmStages?.map(renderStatusIcon)}
                    </div>
                </>
            )}
        </div>
    );
};

export default FtmStages;